import React, { useState, useRef, useEffect, memo } from 'react';
import { API, C } from '../../constants';
import { getInitials, getAvatarColor, timeAgo, formatMsgTime } from '../../utils';

const AdminChatArea = memo(function AdminChatArea({ token, selectedUser, messages, isMobile, onBack, onNewMessage }) {
  const inputRef       = useRef(null);
  const bottomRef      = useRef(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const scrollRef      = useRef(null);
  const isAtBottomRef  = useRef(true);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  useEffect(() => { if (isAtBottomRef.current) bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => {
    isAtBottomRef.current = true;
    setTimeout(() => { bottomRef.current?.scrollIntoView({ behavior: 'instant' }); inputRef.current?.focus(); }, 80);
  }, [selectedUser?.user_id]);

  const sendReply = async () => {
    const content = inputRef.current?.value?.trim();
    if (!content || sending || !selectedUser) return;
    inputRef.current.value = '';
    setSending(true); setSendError('');
    // Si el envío falla se devuelve el texto al input: antes se borraba y el
    // mensaje se perdía sin aviso.
    try {
      const res = await fetch(`${API}/api/chat/send`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ content, receiver_id: selectedUser.user_id }) });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || `Error (${res.status})`);
      }
      onNewMessage();
    } catch (e) {
      if (inputRef.current && !inputRef.current.value) inputRef.current.value = content;
      setSendError(e.message && !e.message.startsWith('Failed') ? `No se envió: ${e.message}` : 'No se envió: error de conexión');
    }
    setSending(false);
    inputRef.current?.focus();
  };

  const handleKey = e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } };

  if (!selectedUser) return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'12px', color:C.textDim }}>
      <p style={{ fontSize:'40px' }}>💬</p>
      <p style={{ fontSize:'14px' }}>Selecciona una conversación</p>
    </div>
  );

  const av = getAvatarColor(selectedUser.user_name);
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', minHeight:0 }}>
      <div style={{ padding:'12px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:'10px', flexShrink:0 }}>
        {isMobile && <button onClick={onBack} style={{ background:'transparent', border:`1px solid ${C.accentBorder}`, color:C.textMuted, padding:'4px 8px', borderRadius:'6px', cursor:'pointer', fontSize:'12px', marginRight:'2px' }}>← Volver</button>}
        <div style={{ position:'relative', flexShrink:0 }}>
          <div style={{ width:'34px', height:'34px', borderRadius:'50%', background:av.bg, color:av.text, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'13px', fontWeight:'600' }}>{getInitials(selectedUser.user_name)}</div>
          <span style={{ position:'absolute', bottom:'1px', right:'1px', width:'8px', height:'8px', borderRadius:'50%', background: selectedUser.is_online ? C.online : C.textDim, border:`2px solid ${C.bgModal}` }} />
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ color:C.text, fontSize:'13px', fontWeight:'500' }}>{selectedUser.user_name}</p>
          <p style={{ color: selectedUser.is_online ? C.online : C.textDim, fontSize:'11px' }}>
            {selectedUser.is_online
              ? `● En línea${selectedUser.session_mins > 0 ? ` · ${selectedUser.session_mins}m` : ''}${selectedUser.watching_now ? ` · ▶ ${selectedUser.watching_now.replace(/\.[^.]+$/, '')}` : ''}`
              : `Última vez ${timeAgo(selectedUser.last_seen)}`}
          </p>
        </div>
      </div>
      <div ref={scrollRef} onScroll={handleScroll} style={{ flex:1, overflowY:'auto', padding:'14px', display:'flex', flexDirection:'column', gap:'8px', scrollbarWidth:'thin', scrollbarColor:`${C.accentBorder} transparent` }}>
        {messages.length === 0 && <div style={{ textAlign:'center', padding:'32px 0', color:C.textDim, fontSize:'13px' }}>Inicio de la conversación</div>}
        {messages.map(m => {
          const isAdmin = m.sender_role === 'admin';
          return (
            <div key={m.id} style={{ display:'flex', flexDirection:'column', alignItems: isAdmin ? 'flex-end' : 'flex-start' }}>
              {!isAdmin && <p style={{ color:C.textDim, fontSize:'10px', marginBottom:'3px', paddingLeft:'4px' }}>{m.sender_name}</p>}
              <div style={{ maxWidth:'80%', padding:'8px 12px', borderRadius: isAdmin ? '14px 14px 3px 14px' : '14px 14px 14px 3px', background: isAdmin ? `linear-gradient(135deg, ${C.accent}, ${C.accentSoft})` : 'rgba(255,255,255,0.07)', border: isAdmin ? 'none' : `1px solid ${C.accentBorder}` }}>
                <p style={{ color: isAdmin ? '#fff' : C.text, fontSize:'13px', lineHeight:'1.5', wordBreak:'break-word' }}>{m.content}</p>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:'4px', marginTop:'3px', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                <p style={{ color:C.textDim, fontSize:'10px' }}>{formatMsgTime(m.created_at)}</p>
                {isAdmin && <span style={{ fontSize:'12px', color: m.read ? '#5dade2' : C.textDim, lineHeight:1, fontWeight:'600' }}>{m.read ? '✓✓' : '✓'}</span>}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      {sendError && <p style={{ color:C.danger, fontSize:'11px', padding:'6px 14px 0', flexShrink:0 }}>{sendError}</p>}
      <div style={{ padding:'10px 14px', borderTop:`1px solid ${C.accentBorder}`, display:'flex', gap:'8px', alignItems:'flex-end', flexShrink:0 }}>
        <textarea ref={inputRef} onKeyDown={handleKey} placeholder={`Responder a ${selectedUser.user_name}...`} rows={1} style={{ flex:1, background:'rgba(255,255,255,0.05)', border:`1px solid ${C.accentBorder}`, borderRadius:'10px', padding:'9px 14px', color:C.text, fontSize:'13px', outline:'none', resize:'none', fontFamily:'Inter, sans-serif', lineHeight:'1.4', maxHeight:'80px', overflow:'auto', scrollbarWidth:'none' }} />
        <button onClick={sendReply} disabled={sending} style={{ width:'36px', height:'36px', background:`linear-gradient(135deg, ${C.accent}, ${C.accentSoft})`, border:'none', borderRadius:'50%', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M2 21l21-9L2 3v7l15 2-15 2z"/></svg>
        </button>
      </div>
    </div>
  );
});

export default AdminChatArea;
